import { create, tsx } from '@dojo/framework/core/vdom';

import * as css from './Image.m.css';

interface BaseImageProperties {
	size?: 'sm' | 'md' | 'lg' | 'xl' | 'icon';
	align?: 'left' | 'right' | 'center';
	/** box shadow for the image */
	shadow?: 'light' | 'dark';
}

interface ImageProperties extends BaseImageProperties {
	/** image src */
	src: string;
	/** alt text for the image */
	alt?: string;
	/** optional high res source */
	highResSrc?: string;
	/** the dimension to constrain the image by */
	constrainedBy?: 'height' | 'width';
	/** if image should be lazily loaded */
	lazy?: boolean;
	/** padding top */
	paddingTop?: 'none' | 'sm' | 'md' | 'lg';
	/** xs: grid x 3, sm: grid x 4, md: grid x 9, grid x 11 */
	paddingBottom?: 'none' | 'xs' | 'sm' | 'md' | 'lg';
	/** Initial width to prevent CLS */
	width?: number;
	/** Initial height to prevent CLS */
	height?: number;
	/** responsive configuration */
	responsive?: {
		/** 500px */
		small?: BaseImageProperties;
		/** 768px */
		medium?: BaseImageProperties;
		/** 900px */
		large?: BaseImageProperties;
	};
}

const factory = create().properties<ImageProperties>();

export const Image = factory(function Image({ properties }) {
	const {
		src,
		highResSrc,
		alt = '',
		shadow,
		size,
		constrainedBy = 'width',
		lazy = false,
		align = 'left',
		paddingBottom = 'none',
		paddingTop = 'none',
		height,
		width,
		responsive
	} = properties();

	const classes = [
		css.root,
		size === 'xl' && css.xl,
		size === 'lg' && css.lg,
		size === 'md' && css.md,
		size === 'sm' && css.sm,
		size === 'icon' && css.icon,
		constrainedBy === 'height' ? css.vertical : css.horizontal,
		align === 'center' && css.center,
		align === 'right' && css.right,
		shadow === 'light' && css.boxShadowLight,
		shadow === 'dark' && css.boxShadowDark,
		paddingBottom === 'xs' && css.paddingBottomXSmall,
		paddingBottom === 'sm' && css.paddingBottomSmall,
		paddingBottom === 'md' && css.paddingBottomMedium,
		paddingBottom === 'lg' && css.paddingBottomLarge,
		paddingTop === 'sm' && css.paddingTopSmall,
		paddingTop === 'md' && css.paddingTopMedium,
		paddingTop === 'lg' && css.paddingTopLarge,
		responsive?.large?.align === 'left' && css.responsiveLargeLeft,
		responsive?.large?.align === 'center' && css.responsiveLargeCenter,
		responsive?.large?.align === 'right' && css.responsiveLargeRight,
		responsive?.large?.size === 'xl' && css.responsiveLargeXl,
		responsive?.large?.size === 'lg' && css.responsiveLargeLg,
		responsive?.large?.size === 'md' && css.responsiveLargeMd,
		responsive?.large?.size === 'sm' && css.responsiveLargeSm,
		responsive?.large?.size === 'icon' && css.responsiveLargeIcon,
		responsive?.large?.shadow === 'dark' && css.responsiveLargeBoxShadowDark,
		responsive?.large?.shadow === 'light' && css.responsiveLargeBoxShadowLight,
		responsive?.medium?.align === 'left' && css.responsiveMediumLeft,
		responsive?.medium?.align === 'center' && css.responsiveMediumCenter,
		responsive?.medium?.align === 'right' && css.responsiveMediumRight,
		responsive?.medium?.size === 'xl' && css.responsiveMediumXl,
		responsive?.medium?.size === 'lg' && css.responsiveMediumLg,
		responsive?.medium?.size === 'md' && css.responsiveMediumMd,
		responsive?.medium?.size === 'sm' && css.responsiveMediumSm,
		responsive?.medium?.size === 'icon' && css.responsiveMediumIcon,
		responsive?.medium?.shadow === 'dark' && css.responsiveMediumBoxShadowDark,
		responsive?.medium?.shadow === 'light' && css.responsiveMediumBoxShadowLight,
		responsive?.small?.align === 'left' && css.responsiveSmallLeft,
		responsive?.small?.align === 'center' && css.responsiveSmallCenter,
		responsive?.small?.align === 'right' && css.responsiveSmallRight,
		responsive?.small?.size === 'xl' && css.responsiveSmallXl,
		responsive?.small?.size === 'lg' && css.responsiveSmallLg,
		responsive?.small?.size === 'md' && css.responsiveSmallMd,
		responsive?.small?.size === 'sm' && css.responsiveSmallSm,
		responsive?.small?.size === 'icon' && css.responsiveSmallIcon,
		responsive?.small?.shadow === 'dark' && css.responsiveSmallBoxShadowDark,
		responsive?.small?.shadow === 'light' && css.responsiveSmallBoxShadowLight
	];

	return (
		<img
			loading={lazy ? 'lazy' : 'eager'}
			classes={classes}
			src={src}
			srcset={highResSrc ? `${highResSrc} 2x` : undefined}
			alt={alt}
			height={height}
			width={width}
		/>
	);
});

export default Image;
